import { useState } from "react";
import {
  Search,
  Plus,
  ShoppingCart,
  AlertTriangle,
  XCircle,
  X,
  Trash2,
  Boxes,
} from "lucide-react";

const initialInventory = [
  {
    id: "INV-1001",
    name: "M8 Hex Bolts (Steel)",
    category: "Hardware",
    qty: 1200,
    unit: "pcs",
    reorderLevel: 300,
    centre: "APIC Visakhapatnam",
    requestedQty: 0,
  },
  {
    id: "INV-1002",
    name: "Li-ion Battery Cell 21700",
    category: "Electrical",
    qty: 45,
    unit: "pcs",
    reorderLevel: 100,
    centre: "APIC Vijayawada",
    requestedQty: 200,
  },
  {
    id: "INV-1003",
    name: "Aluminium Sheet 2mm",
    category: "Raw Material",
    qty: 0,
    unit: "sheets",
    reorderLevel: 20,
    centre: "APIC Tirupati",
    requestedQty: 50,
  },
  {
    id: "INV-1004",
    name: "PCB Connector 4-Pin",
    category: "Electrical",
    qty: 860,
    unit: "pcs",
    reorderLevel: 200,
    centre: "APIC Tirupati",
    requestedQty: 0,
  },
  {
    id: "INV-1005",
    name: "Servo Motor MG996R",
    category: "Robotics",
    qty: 12,
    unit: "pcs",
    reorderLevel: 15,
    centre: "APIC Kurnool",
    requestedQty: 30,
  },
  {
    id: "INV-1006",
    name: "Silicone Tubing 6mm",
    category: "Consumable",
    qty: 400,
    unit: "meters",
    reorderLevel: 100,
    centre: "APIC Visakhapatnam",
    requestedQty: 0,
  },
];

const CATEGORIES = ["Hardware", "Electrical", "Raw Material", "Robotics", "Consumable"];

function getStatus(item) {
  if (item.qty === 0) return "Out of Stock";
  if (item.qty <= item.reorderLevel) return "Low Stock";
  return "In Stock";
}

const STATUS_STYLES = {
  "In Stock": "bg-green-100 text-green-700",
  "Low Stock": "bg-orange-100 text-orange-700",
  "Out of Stock": "bg-red-100 text-red-700",
  "Requested": "bg-violet-100 text-violet-700",
};

const STATUS_FILTERS = ["All", "In Stock", "Low Stock", "Out of Stock", "Requested"];

function StatCard({ icon, label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${tone}`}>
        {icon}
      </div>
      <div>
        <p className="text-2xl font-extrabold text-slate-900 leading-tight">{value}</p>
        <p className="text-sm text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-0 sm:px-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-extrabold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default function InventoryScreen() {
  const [inventory, setInventory] = useState(initialInventory);
  const [search, setSearch] = useState("");
  const [activeStatus, setActiveStatus] = useState("All");
  const [showAddModal, setShowAddModal] = useState(false);
  const [requestTarget, setRequestTarget] = useState(null);

  const [newItem, setNewItem] = useState({
    name: "",
    category: CATEGORIES[0],
    qty: "",
    unit: "pcs",
    reorderLevel: "",
    centre: "",
  });
  const [requestQty, setRequestQty] = useState("");

  const term = search.trim().toLowerCase();

  const filtered = inventory.filter((item) => {
    const status = item.requestedQty > 0 ? "Requested" : getStatus(item);
    const matchesStatus = activeStatus === "All" || status === activeStatus;
    const matchesSearch =
      !term ||
      item.name.toLowerCase().includes(term) ||
      item.id.toLowerCase().includes(term) ||
      item.category.toLowerCase().includes(term);
    return matchesStatus && matchesSearch;
  });

  const totalItems = inventory.length;
  const lowStock = inventory.filter((i) => getStatus(i) === "Low Stock").length;
  const outOfStock = inventory.filter((i) => getStatus(i) === "Out of Stock").length;
  const requestedCount = inventory.filter((i) => i.requestedQty > 0).length;

  function handleAddItem(e) {
    e.preventDefault();
    if (!newItem.name || !newItem.qty || !newItem.centre) return;
    const id = "INV-" + (1000 + inventory.length + 1);
    setInventory([
      ...inventory,
      {
        id,
        name: newItem.name,
        category: newItem.category,
        qty: Number(newItem.qty),
        unit: newItem.unit || "pcs",
        reorderLevel: Number(newItem.reorderLevel) || 0,
        centre: newItem.centre,
        requestedQty: 0,
      },
    ]);
    setNewItem({ name: "", category: CATEGORIES[0], qty: "", unit: "pcs", reorderLevel: "", centre: "" });
    setShowAddModal(false);
  }

  function handleRequestSubmit(e) {
    e.preventDefault();
    if (!requestQty || !requestTarget) return;
    setInventory((prev) =>
      prev.map((item) =>
        item.id === requestTarget.id
          ? { ...item, requestedQty: item.requestedQty + Number(requestQty) }
          : item
      )
    );
    setRequestQty("");
    setRequestTarget(null);
  }

  function handleDelete(id) {
    setInventory((prev) => prev.filter((item) => item.id !== id));
  }

  return (
    <div className="" style={{height:'95vh',overflow:'auto',scrollbarWidth:"none"}}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-1">
              Inventory
            </h1>
            <p className="text-slate-500">{totalItems} items across 4 APIC centres</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center justify-center gap-2 bg-gradient-to-br from-indigo-500 to-blue-500 text-white font-semibold px-5 py-3 rounded-xl shadow-sm hover:opacity-95 whitespace-nowrap"
          >
            <Plus size={18} />
            Add Item
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Boxes size={20} className="text-blue-600" />}
            label="Total Items"
            value={totalItems}
            tone="bg-blue-50"
          />
          <StatCard
            icon={<AlertTriangle size={20} className="text-orange-600" />}
            label="Low Stock"
            value={lowStock}
            tone="bg-orange-50"
          />
          <StatCard
            icon={<XCircle size={20} className="text-red-600" />}
            label="Out of Stock"
            value={outOfStock}
            tone="bg-red-50"
          />
          <StatCard
            icon={<ShoppingCart size={20} className="text-violet-600" />}
            label="Requested"
            value={requestedCount}
            tone="bg-violet-50"
          />
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 mb-5 shadow-sm">
          <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-3 mb-4">
            <Search size={18} className="text-slate-400 flex-shrink-0" />
            <input
              type="text"
              placeholder="Search item name, ID, or category..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 outline-none text-sm text-slate-900 placeholder:text-slate-400"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((s) => {
              const count =
                s === "All"
                  ? inventory.length
                  : inventory.filter((i) => (i.requestedQty > 0 ? "Requested" : getStatus(i)) === s)
                      .length;
              const active = activeStatus === s;
              return (
                <button
                  key={s}
                  onClick={() => setActiveStatus(s)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    active
                      ? "bg-gradient-to-br from-indigo-500 to-blue-500 text-white border-transparent"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  {s} <span className="opacity-75 font-bold text-xs">{count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-4 px-1">
          Showing {filtered.length} of {inventory.length} items
        </p>

        {/* Desktop table */}
        <div className="hidden md:block bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-left text-slate-500">
                <th className="px-5 py-3 font-semibold">Item ID</th>
                <th className="px-5 py-3 font-semibold">Name</th>
                <th className="px-5 py-3 font-semibold">Category</th>
                <th className="px-5 py-3 font-semibold">Quantity</th>
                <th className="px-5 py-3 font-semibold">Centre</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-14 text-slate-500">
                    No items found. Try a different search or filter.
                  </td>
                </tr>
              ) : (
                filtered.map((item) => {
                  const status = item.requestedQty > 0 ? "Requested" : getStatus(item);
                  return (
                    <tr key={item.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                      <td className="px-5 py-4 font-mono text-xs text-slate-600">{item.id}</td>
                      <td className="px-5 py-4 font-semibold text-slate-900">{item.name}</td>
                      <td className="px-5 py-4 text-slate-600">{item.category}</td>
                      <td className="px-5 py-4 text-slate-900">
                        {item.qty} {item.unit}
                        {item.requestedQty > 0 && (
                          <span className="block text-xs text-violet-600 font-semibold">
                            +{item.requestedQty} requested
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-slate-600">{item.centre}</td>
                      <td className="px-5 py-4">
                        <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${STATUS_STYLES[status]}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setRequestTarget(item)}
                            className="flex items-center gap-1 text-xs font-semibold text-blue-600 border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-50"
                          >
                            <ShoppingCart size={13} />
                            Request
                          </button>
                          <button
                            onClick={() => handleDelete(item.id)}
                            className="text-slate-400 hover:text-red-600 p-1.5"
                            title="Delete item"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list */}
        <div className="md:hidden flex flex-col gap-4">
          {filtered.length === 0 ? (
            <div className="text-center py-14 px-5 bg-white border border-dashed border-slate-200 rounded-2xl text-slate-500">
              No items found. Try a different search or filter.
            </div>
          ) : (
            filtered.map((item) => {
              const status = item.requestedQty > 0 ? "Requested" : getStatus(item);
              return (
                <div key={item.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <span className="font-mono text-xs border border-slate-200 rounded-full px-3 py-1 text-slate-600">
                      {item.id}
                    </span>
                    <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${STATUS_STYLES[status]}`}>
                      {status}
                    </span>
                  </div>
                  <p className="text-lg font-extrabold text-slate-900">{item.name}</p>
                  <p className="text-sm text-slate-500 mb-3">
                    {item.category} · {item.centre}
                  </p>
                  <div className="flex justify-between items-center text-sm mb-4">
                    <span className="text-slate-500">Quantity</span>
                    <span className="font-bold text-slate-900 text-right">
                      {item.qty} {item.unit}
                      {item.requestedQty > 0 && (
                        <span className="block text-xs text-violet-600 font-semibold">
                          +{item.requestedQty} requested
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => setRequestTarget(item)}
                      className="flex-1 flex items-center justify-center gap-1.5 text-sm font-semibold text-blue-600 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-50"
                    >
                      <ShoppingCart size={14} />
                      Request
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="text-slate-400 hover:text-red-600 p-2 border border-slate-200 rounded-lg"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Add Item Modal */}
      {showAddModal && (
        <Modal title="Add Inventory Item" onClose={() => setShowAddModal(false)}>
          <form onSubmit={handleAddItem} className="flex flex-col gap-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">Item Name</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="e.g. M6 Hex Nuts"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Category</label>
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Unit</label>
                <input
                  type="text"
                  value={newItem.unit}
                  onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                  placeholder="pcs / meters / kg"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Quantity</label>
                <input
                  type="number"
                  min="0"
                  value={newItem.qty}
                  onChange={(e) => setNewItem({ ...newItem, qty: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1">Reorder Level</label>
                <input
                  type="number"
                  min="0"
                  value={newItem.reorderLevel}
                  onChange={(e) => setNewItem({ ...newItem, reorderLevel: e.target.value })}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">APIC Centre</label>
              <input
                type="text"
                value={newItem.centre}
                onChange={(e) => setNewItem({ ...newItem, centre: e.target.value })}
                placeholder="e.g. APIC Visakhapatnam"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                required
              />
            </div>
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-slate-200 rounded-lg py-2.5 font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-br from-indigo-500 to-blue-500 text-white rounded-lg py-2.5 font-semibold hover:opacity-95"
              >
                Add Item
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Request Item Modal */}
      {requestTarget && (
        <Modal title={`Request: ${requestTarget.name}`} onClose={() => setRequestTarget(null)}>
          <form onSubmit={handleRequestSubmit} className="flex flex-col gap-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-600">
              Current stock:{" "}
              <span className="font-bold text-slate-900">
                {requestTarget.qty} {requestTarget.unit}
              </span>{" "}
              at {requestTarget.centre}
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-700 block mb-1">
                Quantity to request ({requestTarget.unit})
              </label>
              <input
                type="number"
                min="1"
                value={requestQty}
                onChange={(e) => setRequestQty(e.target.value)}
                placeholder="e.g. 100"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-400"
                required
                autoFocus
              />
            </div>
            <div className="flex gap-3 mt-2">
              <button
                type="button"
                onClick={() => setRequestTarget(null)}
                className="flex-1 border border-slate-200 rounded-lg py-2.5 font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 bg-gradient-to-br from-indigo-500 to-blue-500 text-white rounded-lg py-2.5 font-semibold hover:opacity-95"
              >
                Submit Request
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}